import { queryOptions, useQueries } from '@tanstack/react-query';
import { useAuth } from '../../../auth/contexts/AuthContext';
import { useForBindingId } from '../../../forBinding/contexts/ForBindingContext';
import api from '../../http';
import { OCR_TIMEOUT_MS } from './ocr.constants';

export interface OcrMeasure {
	value: number;
	unit: string;
}

export interface OcrSoilSample {
	sampleId?: string;
	sampleIndex: number;
	sampleLabId: string;
	sampleLabel: string;
	depthCm: string;
	Pm1: OcrMeasure;
	Pres: OcrMeasure;
	K: OcrMeasure;
	Ca: OcrMeasure;
	Mg: OcrMeasure;
	S: OcrMeasure;
	B: OcrMeasure;
	Cu: OcrMeasure;
	Fe: OcrMeasure;
	Mn: OcrMeasure;
	Zn: OcrMeasure;
	pHagua: OcrMeasure;
	pHCaCl2: OcrMeasure;
	Al: OcrMeasure;
	HAl: OcrMeasure;
	MO: OcrMeasure;
	Na: OcrMeasure;
	Prem: OcrMeasure;
	SB: OcrMeasure;
	ctcE: OcrMeasure;
	ctcT: OcrMeasure;
	CaMg: OcrMeasure;
	satSB: OcrMeasure;
	satAl: OcrMeasure;
	satCa: OcrMeasure;
	satMg: OcrMeasure;
	satK: OcrMeasure;
}

export interface OcrFoliarSample {
	sampleId?: string;
	sampleIndex: number;
	sampleLabId: string;
	sampleLabel: string;
	N: OcrMeasure;
	P: OcrMeasure;
	K: OcrMeasure;
	Ca: OcrMeasure;
	Mg: OcrMeasure;
	S: OcrMeasure;
	B: OcrMeasure;
	Cu: OcrMeasure;
	Fe: OcrMeasure;
	Mn: OcrMeasure;
	Zn: OcrMeasure;
}

export interface OcrVerifyResult<TSample extends OcrSoilSample | OcrFoliarSample> {
	jobId: string;
	samples: TSample[];
}

export interface OcrWarning {
	sampleId: string;
	message: string;
}

export interface OcrVerifyJobResponse<TSample extends OcrSoilSample | OcrFoliarSample> {
	jobId: string;
	status: 'queued' | 'processing' | 'done' | 'failed';
	warning?: boolean;
	warningMessage?: string;
	warnings?: OcrWarning[];
	analysisType: 'soil' | 'foliar';
	createdAt: string;
	updatedAt: string;
	queueName?: string;
	fileType?: string;
	sentAt?: string;
	error?: { message?: string };
	result?: OcrVerifyResult<TSample>;
}

export const useGetOcrVerify = <TSample extends OcrSoilSample | OcrFoliarSample = OcrSoilSample>(
	jobIds: ReadonlyArray<string | undefined>,
	enabled = true
) => {
	const { user, isLoading } = useAuth();
	const forBinding = useForBindingId();
	const userId = !isLoading ? user?._id : undefined;
	const profileId = userId ? forBinding ?? userId : undefined;
	return useQueries({
		queries: jobIds.map((jobId) => queryOptions({
			queryKey: ['ocrVerify', 'profile', profileId, jobId],
			queryFn: async ({ signal }) => {
				const { data } = await api.get<OcrVerifyJobResponse<TSample>>(
					`/ocr/verifyJob/${jobId}`,
					{ timeout: OCR_TIMEOUT_MS, signal, params: { forBinding } }
				);
				return data;
			},
			enabled: !!userId && !!jobId && enabled,
			refetchInterval: (query) => {
				const status = query.state.data?.status;
				return status === 'done' || status === 'failed' ? false : 3000;
			},
			refetchIntervalInBackground: true,
		})),
	});
};
